// Generalized month-grid calendar showing per-day occurrence counts,
// optionally tinted by intensity (0–10 band). Ported from BowelCalendar.tsx.
// D-IE-14/D-IE-18: also supports an "active day" tint for a symptom's own
// span and a single comparison-symptom overlay line (ITEM_REPORT_ENHANCEMENTS_PLAN.md).

import { useEffect, useMemo, useRef, useState } from "react";
import { localCalendarDate } from "@medassist/shared";

/** Default overlay-line color (D-IE-12), used only until the live --accent
 *  value is read (see useOverlayColor below). */
export const OVERLAY_COLOR = "#8b5cf6";

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  else if (max === gn) h = ((bn - rn) / d + 2) * 60;
  else h = ((rn - gn) / d + 4) * 60;
  return [h, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Reads the live --accent value (set per-patient on a Shell ancestor) and
 *  derives the overlay line's color as the accent's complementary hue
 *  (rotated 180deg), so the line always reads as visually distinct from the
 *  accent-colored occurrence badge — not just "far enough" per a distance
 *  threshold, which still let some pairs (e.g. blue accent vs. the prior
 *  fixed violet overlay) look too similar at small sizes. */
function useOverlayColor(ref: React.RefObject<HTMLElement | null>): string {
  const [color, setColor] = useState(OVERLAY_COLOR);
  useEffect(() => {
    const accent = ref.current
      ? getComputedStyle(ref.current).getPropertyValue("--accent").trim()
      : "";
    const rgb = accent ? hexToRgb(accent) : null;
    if (!rgb) {
      setColor(OVERLAY_COLOR);
      return;
    }
    const [h, s, l] = rgbToHsl(rgb);
    // Keep saturation/lightness in a range that stays legible as a thin line
    // in both light and dark mode, even if the accent itself is muted.
    const s2 = Math.max(s, 0.6);
    const l2 = Math.min(Math.max(l, 0.45), 0.55);
    setColor(hslToHex((h + 180) % 360, s2, l2));
  }, [ref]);
  return color;
}

interface OverlayProps {
  label: string;
  color: string; // always OVERLAY_COLOR
  activeDays: Map<string, number>; // date -> severity, for the cell tooltip
}

interface Props {
  counts: Map<string, number>;
  intensityByDay?: Map<string, number>;
  /** Days on which THIS item's symptom was active (date -> severity). Renders a
   *  tinted cell background so a symptom's own span reads as continuous (D-IE-14). */
  activeDays?: Map<string, number>;
  /** One comparison symptom drawn as a colored line under the day number (D-IE-10/11/12). */
  overlay?: OverlayProps;
  timezone: string;
  onDayClick?: (date: string) => void;
}

const DOW_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// 0–10 severity → Tailwind bg class (≤3 green, ≤6 amber, ≥7 red)
function intensityBg(val: number): string {
  if (val <= 3) return "bg-emerald-400 dark:bg-emerald-500";
  if (val <= 6) return "bg-amber-400 dark:bg-amber-500";
  return "bg-red-400 dark:bg-red-500";
}

/** `date` (YYYY-MM-DD) shifted by `offsetDays` via a Date.UTC walk, so a span's
 *  rounded caps are computed from real calendar-adjacent dates, never string math. */
function utcDayWalk(date: string, offsetDays: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + offsetDays);
  return dt.toISOString().slice(0, 10);
}

export function CountCalendar({ counts, intensityByDay, activeDays, overlay, timezone, onDayClick }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const resolvedOverlayColor = useOverlayColor(rootRef);
  const today = localCalendarDate(new Date().toISOString(), timezone);
  const [yearMonth, setYearMonth] = useState<string>(today.slice(0, 7));

  const [year, month] = yearMonth.split("-").map(Number) as [number, number];

  // Only counts for the displayed month (avoid re-rendering the whole map)
  const monthCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const [k, v] of counts) {
      if (k.startsWith(yearMonth)) m.set(k, v);
    }
    return m;
  }, [counts, yearMonth]);

  function prevMonth() {
    const d = new Date(year, month - 2, 1);
    setYearMonth(d.toISOString().slice(0, 7));
  }
  function nextMonth() {
    const d = new Date(year, month, 1);
    setYearMonth(d.toISOString().slice(0, 7));
  }

  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells: (number | null)[] = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="select-none" ref={rootRef}>
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={prevMonth}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0 sm:p-1.5"
          aria-label="Previous month"
        >
          &#8249;
        </button>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{monthLabel}</span>
        <button
          onClick={nextMonth}
          disabled={yearMonth >= today.slice(0, 7)}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0 sm:p-1.5"
          aria-label="Next month"
        >
          &#8250;
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-px text-center">
        {DOW_LABELS.map((d) => (
          <div key={d} className="text-[10px] font-medium text-gray-400">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`pad-${idx}`} />;
          const dateStr = `${yearMonth}-${String(day).padStart(2, "0")}`;
          const count = monthCounts.get(dateStr) ?? 0;
          const intensity = intensityByDay?.get(dateStr);
          const isToday = dateStr === today;
          const isFuture = dateStr > today;
          const hasData = count > 0;
          const isActive = activeDays?.has(dateStr) ?? false;
          const clickable = (hasData || isActive) && !isFuture;

          const overlaySeverity = overlay?.activeDays.get(dateStr);
          const overlayActive = overlaySeverity !== undefined;
          const prevOverlayActive = overlay ? overlay.activeDays.has(utcDayWalk(dateStr, -1)) : false;
          const nextOverlayActive = overlay ? overlay.activeDays.has(utcDayWalk(dateStr, 1)) : false;

          let ariaLabel = `${dateStr}: ${count} occurrence${count !== 1 ? "s" : ""}`;
          if (overlayActive) ariaLabel += `, ${overlay!.label} active (severity ${overlaySeverity})`;

          return (
            <button
              key={dateStr}
              onClick={() => clickable && onDayClick?.(dateStr)}
              disabled={!clickable}
              className={[
                "relative flex min-h-[44px] flex-col items-center justify-center gap-0.5 rounded-md p-1 text-xs",
                isToday ? "ring-2 ring-stone-400 dark:ring-stone-500" : "",
                isActive
                  ? "bg-[color:var(--accent,#2563eb)]/10 ring-1 ring-inset ring-[color:var(--accent,#2563eb)]/20"
                  : "",
                isFuture
                  ? "opacity-30"
                  : clickable
                    ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    : "cursor-default",
              ].join(" ")}
              aria-label={ariaLabel}
            >
              <span
                className={
                  "text-xs font-medium " +
                  (isToday
                    ? "text-stone-700 dark:text-stone-300"
                    : "text-gray-600 dark:text-gray-400")
                }
              >
                {day}
              </span>
              {hasData && (
                <span
                  className={[
                    "flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1 text-[11px] font-semibold text-white",
                    intensity !== undefined
                      ? intensityBg(intensity)
                      : "bg-[color:var(--accent,#2563eb)]",
                  ].join(" ")}
                  aria-hidden="true"
                >
                  {count}
                </span>
              )}
              {overlayActive && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-[3px] h-[3px]"
                  style={{
                    backgroundColor: resolvedOverlayColor,
                    borderTopLeftRadius: prevOverlayActive ? 0 : 9999,
                    borderBottomLeftRadius: prevOverlayActive ? 0 : 9999,
                    borderTopRightRadius: nextOverlayActive ? 0 : 9999,
                    borderBottomRightRadius: nextOverlayActive ? 0 : 9999,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {(activeDays || overlay) && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <span
              className="h-3 w-3 rounded-full bg-[color:var(--accent,#2563eb)]"
              aria-hidden="true"
            />
            Occurrences
          </span>
          {activeDays && (
            <span className="flex items-center gap-1.5">
              <span
                className="h-3 w-3 rounded-sm bg-[color:var(--accent,#2563eb)]/20 ring-1 ring-inset ring-[color:var(--accent,#2563eb)]/40"
                aria-hidden="true"
              />
              Active
            </span>
          )}
          {overlay && (
            <span className="flex items-center gap-1.5">
              <span
                className="h-[3px] w-4 rounded-full"
                style={{ backgroundColor: resolvedOverlayColor }}
                aria-hidden="true"
              />
              {overlay.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
